import React, { Component } from 'react';
import axios from 'axios';
import { CircularProgress } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {
	Page,
	Text,
	View,
	Font,
	Document,
	StyleSheet,
	PDFDownloadLink,
} from '@react-pdf/renderer';
import moment from 'moment/min/moment-with-locales';
import phoneFormatter from 'phone-formatter';
import getAmountLabel from '../../utils/getAmountLabel';
import dictionary from '../../utils/dictionary';

const acceptableLanguages = ['en', 'fr', 'es'];

const formatDate = ({ date }) =>
	moment.unix(date).format('MMM D, YYYY, h:mm A');

const formatPhone = ({ phone }) => {
	if (phone) {
		return phoneFormatter.format(phone, '(NNN) NNN NNNN');
	}
	return '';
};

// Create styles
const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		paddingTop: 50,
	},
	section: { paddingLeft: 50, paddingRight: 50, paddingTop: 15 },
	leftColumn: {
		flexDirection: 'column',
		width: 350,
		paddingLeft: 50,
		paddingRight: 15,
	},
	rightColumn: {},
	image: { maxWidth: '140px' },
	textBlock: {
		paddingTop: 15,
		paddingBottom: 15,
	},
	header: {
		fontFamily: 'Open Sans',
	},
	text: {
		fontSize: 10,
		paddingBottom: 2,
		fontFamily: 'Open Sans',
	},
	textBold: {
		fontSize: 10,
		paddingBottom: 2,
		fontFamily: 'Open Sans 600',
	},
	table: {
		display: 'table',
		width: 'auto',
		borderStyle: 'solid',
		borderWidth: 1,
		borderRightWidth: 0,
		borderBottomWidth: 0,
	},
	tableRow: {
		margin: 'auto',
		flexDirection: 'row',
	},
	tableCol: {
		width: '25%',
		borderStyle: 'solid',
		borderWidth: 1,
		borderLeftWidth: 0,
		borderTopWidth: 0,
	},
	tableCell: {
		margin: 2,
		fontSize: 10,
		fontFamily: 'Open Sans',
	},
});

Font.register({
	family: 'Open Sans',
	fonts: [
		{
			src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
		},
	],
});

Font.register({
	family: 'Open Sans 600',
	fonts: [
		{
			src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf',
			fontWeight: 600,
		},
	],
});

class Receipt extends Component {
	state = { loading: false, error: false, receiptData: null };

	async componentDidMount() {
		await this.setLanguage();
		this.setTitle();
		await this.getReceiptData();
	}

	setLanguage = async () => {
		const { language } = this.props;

		await this.setState({ language });
	};

	setTitle = () => {
		const { language } = this.state;
		const { title } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		document.title = title;
	};

	getReceiptData = async () => {
		try {
			const { _invoice } = this.props;
			this.setState({ loading: true });

			const res = await axios.get(`/api/receipts/${_invoice}`);

			const { receiptData } = res.data;

			this.setState({ receiptData, loading: false });
		} catch (err) {
			console.log(err);
			this.setState({ error: true, loading: false });
		}
	};

	renderAddress = (supportAddress) => {
		if (!supportAddress) {
			return ``;
		}

		if (supportAddress && supportAddress.postal_code) {
			return `${supportAddress.city}, ${supportAddress.state}, ${supportAddress.country}, ${supportAddress.postal_code}`;
		}

		return `${supportAddress.city}, ${supportAddress.state}, ${supportAddress.country}`;
	};

	// eslint-disable-next-line camelcase
	renderRefund = (refunds, payment_method_details) => {
		const { language } = this.state;

		// get labels from the dictionary for the appropriate language
		const { refundText } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		const { currency } = this.props;

		return refunds.map((refund) => {
			return (
				<View style={styles.tableRow} key={refund.id}>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{formatDate({
								date: refund.created,
							})}
						</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{refundText}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{payment_method_details.card.brand.replace(
								/^./,
								payment_method_details.card.brand[0].toUpperCase()
							)}{' '}
							{payment_method_details.card.last4}
						</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{getAmountLabel({
								amount: refund.amount,
								currency,
							})}{' '}
							{refund.currency.toUpperCase()}{' '}
						</Text>
					</View>
				</View>
			);
		});
	};

	renderPaymentLabel = ({ charge, hasConvenienceFee }) => {
		if (hasConvenienceFee) {
			return <Text style={styles.tableCell}>Total</Text>;
		}

		return (
			<Text style={styles.tableCell}>
				{charge.payment_method_details.card.brand.replace(
					/^./,
					charge.payment_method_details.card.brand[0].toUpperCase()
				)}{' '}
				{charge.payment_method_details.card.last4}
			</Text>
		);
	};

	renderTableRows = (
		charges,
		hasConvenienceFee,
		originalAmount,
		convenienceFee
	) => {
		const { language } = this.state;

		// get labels from the dictionary for the appropriate language
		const { creditCardText } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		if (hasConvenienceFee) {
			return (
				<div>
					{hasConvenienceFee && (
						<div>
							<View style={styles.tableRow}>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{formatDate({
											date: charges[0].created,
										})}
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{creditCardText}
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										Base Amount
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{getAmountLabel({
											amount: originalAmount,
											currency: this.props.currency,
										})}{' '}
										{charges.length > 0
											? charges[0].currency.toUpperCase()
											: ''}
									</Text>
								</View>
							</View>
							<View style={styles.tableRow}>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{formatDate({
											date: charges[0].created,
										})}
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{creditCardText}
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										Convenience Fee
									</Text>
								</View>
								<View style={styles.tableCol}>
									<Text style={styles.tableCell}>
										{getAmountLabel({
											amount: convenienceFee,
											currency: this.props.currency,
										})}{' '}
										{charges.length > 0
											? charges[0].currency.toUpperCase()
											: ''}
									</Text>
								</View>
							</View>
						</div>
					)}
					{charges.map((charge) => {
						return (
							<div>
								<View style={styles.tableRow} key={charge.id}>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{formatDate({
												date: charge.created,
											})}
										</Text>
									</View>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{creditCardText}
										</Text>
									</View>
									<View style={styles.tableCol}>
										{this.renderPaymentLabel({
											charge,
											hasConvenienceFee,
										})}
									</View>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{getAmountLabel({
												amount: charge.amount,
												currency: this.props.currency,
											})}{' '}
											{charge.currency.toUpperCase()}{' '}
										</Text>
									</View>
								</View>
								{this.renderRefund(
									charge.refunds.data,
									charge.payment_method_details
								)}
							</div>
						);
					})}
				</div>
			);
		}

		return charges.map((charge) => {
			return (
				<div>
					<View style={styles.tableRow} key={charge.id}>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{formatDate({
									date: charge.created,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{creditCardText}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{charge.payment_method_details.card.brand.replace(
									/^./,
									charge.payment_method_details.card.brand[0].toUpperCase()
								)}{' '}
								{charge.payment_method_details.card.last4}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{getAmountLabel({
									amount: charge.amount,
									currency: this.props.currency,
								})}{' '}
								{charge.currency.toUpperCase()}{' '}
							</Text>
						</View>
					</View>
					{this.renderRefund(
						charge.refunds.data,
						charge.payment_method_details
					)}
				</div>
			);
		});
	};

	renderTable = (
		paymentIntent,
		hasConvenienceFee,
		originalAmount,
		convenienceFee
	) => {
		const { language } = this.state;

		// get labels from the dictionary for the appropriate language
		const { date, type, detail, amount } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		if (hasConvenienceFee) {
			return (
				<View style={styles.table}>
					<View style={styles.tableRow}>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>{date}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>{type}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>{detail}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>{amount}</Text>
						</View>
					</View>
					{this.renderTableRows(
						paymentIntent.charges.data,
						hasConvenienceFee,
						originalAmount,
						convenienceFee
					)}
				</View>
			);
		}

		return (
			<View style={styles.table}>
				<View style={styles.tableRow}>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{date}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{type}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{detail}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{amount}</Text>
					</View>
				</View>
				{this.renderTableRows(paymentIntent.charges.data)}
			</View>
		);
	};

	renderBillingDetails = (paymentIntent) => {
		if (!paymentIntent.payment_method.billing_details.address.city) {
			return '';
		}

		return `${paymentIntent.payment_method.billing_details.address.city}, ${paymentIntent.payment_method.billing_details.address.state}, ${paymentIntent.payment_method.billing_details.address.country}, ${paymentIntent.payment_method.billing_details.address.postal_code}`;
	};

	// Create Document Component
	renderReceipt = ({ paymentIntent, customer }) => {
		const { language } = this.state;
		const { name, address, phone, username } = this.props.invoiceData;
		const { hasConvenienceFee, originalAmount, convenienceFee } =
			this.props;

		// get labels from the dictionary for the appropriate language
		const {
			paidBy,
			receipt,
			referenceNumber,
			description,
			datePaid,
			paymentMethod,
		} =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		if (hasConvenienceFee) {
			return (
				<Document>
					<Page size="A4">
						<View style={styles.container}>
							<View style={styles.leftColumn}>
								<Text style={styles.header}>{name}</Text>
								<div style={styles.textBlock}>
									<Text style={styles.text}>{address}</Text>
									<Text style={styles.text}>
										{formatPhone({
											phone,
										})}
									</Text>
									<Text style={styles.text}>{username}</Text>
								</div>
								<div style={styles.textBlock}>
									<Text style={styles.textBold}>
										{paidBy.toUpperCase()}
									</Text>
									<Text style={styles.text}>
										{customer.name}
									</Text>
									<Text style={styles.text}>
										{this.renderBillingDetails(
											paymentIntent
										)}
									</Text>
									<Text style={styles.text}>
										{formatPhone({
											phone: customer.phone,
										})}
									</Text>
									<Text style={styles.text}>
										{
											paymentIntent.payment_method
												.billing_details.email
										}
									</Text>
								</div>
							</View>
							<View style={styles.rightColumn}>
								<Text style={styles.header}>
									{receipt.toUpperCase()}
								</Text>
								<div style={styles.textBlock}>
									<Text style={styles.text}>
										<strong>{referenceNumber}: </strong>
										{
											paymentIntent.metadata
												.reference_number
										}
									</Text>
									<Text style={styles.text}>
										{description}:{' '}
										{paymentIntent.description}
									</Text>
									<Text style={styles.text}>
										{datePaid}:{' '}
										{formatDate({
											date: paymentIntent.charges.data[0]
												.created,
										})}
									</Text>
									<Text style={styles.text}>
										{paymentMethod}:{' '}
										{paymentIntent.payment_method.card.brand.replace(
											/^./,
											paymentIntent.payment_method.card.brand[0].toUpperCase()
										)}{' '}
										{
											paymentIntent.payment_method.card
												.last4
										}
									</Text>
								</div>
							</View>
						</View>

						<View style={styles.section}>
							{this.renderTable(
								paymentIntent,
								hasConvenienceFee,
								originalAmount,
								convenienceFee
							)}
						</View>
					</Page>
				</Document>
			);
		}

		return (
			<Document>
				<Page size="A4">
					<View style={styles.container}>
						<View style={styles.leftColumn}>
							<Text style={styles.header}>{name}</Text>
							<div style={styles.textBlock}>
								<Text style={styles.text}>{address}</Text>
								<Text style={styles.text}>
									{formatPhone({
										phone,
									})}
								</Text>
								<Text style={styles.text}>{username}</Text>
							</div>
							<div style={styles.textBlock}>
								<Text style={styles.textBold}>
									{paidBy.toUpperCase()}
								</Text>
								<Text style={styles.text}>{customer.name}</Text>
								<Text style={styles.text}>
									{this.renderBillingDetails(paymentIntent)}
								</Text>
								<Text style={styles.text}>
									{formatPhone({
										phone: customer.phone,
									})}
								</Text>
								<Text style={styles.text}>
									{
										paymentIntent.payment_method
											.billing_details.email
									}
								</Text>
							</div>
						</View>
						<View style={styles.rightColumn}>
							<Text style={styles.header}>
								{receipt.toUpperCase()}
							</Text>
							<div style={styles.textBlock}>
								<Text style={styles.text}>
									<strong>{referenceNumber}: </strong>
									{paymentIntent.metadata.reference_number}
								</Text>
								<Text style={styles.text}>
									{description}: {paymentIntent.description}
								</Text>
								<Text style={styles.text}>
									{datePaid}:{' '}
									{formatDate({
										date: paymentIntent.charges.data[0]
											.created,
									})}
								</Text>
								<Text style={styles.text}>
									{paymentMethod}:{' '}
									{paymentIntent.payment_method.card.brand.replace(
										/^./,
										paymentIntent.payment_method.card.brand[0].toUpperCase()
									)}{' '}
									{paymentIntent.payment_method.card.last4}
								</Text>
							</div>
						</View>
					</View>

					<View style={styles.section}>
						{this.renderTable(paymentIntent)}
					</View>
				</Page>
			</Document>
		);
	};

	renderLoading = () => {
		return (
			<div
				style={{
					display: 'flex',
					height: '60%',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress size={20} />
			</div>
		);
	};

	renderError = () => {
		const { language } = this.state;
		const { errorTitle, errorMessage } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		return (
			<Grid
				container
				direction="column"
				justify="center"
				alignItems="center"
				style={{ height: '60%' }}
			>
				<Grid item>
					<h3>{errorTitle}</h3>
				</Grid>

				<Grid item>
					<div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
						{errorMessage}
					</div>
				</Grid>
			</Grid>
		);
	};

	render() {
		const { receiptData, loading, error, language } = this.state;

		const { downloadReceipt, receipt } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];
		// set the language of moment
		moment.locale(language || 'en');

		if (loading) {
			return this.renderLoading();
		}

		if (error) {
			return this.renderError();
		}

		if (receiptData) {
			const { paymentIntent, customer, businessProfile } = receiptData;

			return (
				<div>
					<PDFDownloadLink
						document={this.renderReceipt({
							paymentIntent,
							customer,
							businessProfile,
						})}
						style={{
							color: 'rgba(0,0,0,0.87)',
							fontWeight: 500,
						}}
						fileName={`${receipt} ${moment().format(
							'MMM DD YYYY'
						)}`}
					>
						{loading ? this.renderLoading() : downloadReceipt}
					</PDFDownloadLink>
				</div>
			);
		}

		return <span />;
	}
}

export default Receipt;



// WEBPACK FOOTER //
// ./src/components/payments/Receipt.js